// src/components/modals/ViewPromotionModal.jsx
import { X, Tag, Users, Calendar, Package, Clock, Target, Star } from 'lucide-react'
import { PromotionModel, PROMOTION_TYPE_CONFIG, PRIORITY_CONFIG } from '../../utils/promotionModel'

const ViewPromotionModal = ({ isOpen, onClose, promotion }) => {
  // Don't render if not open or no promotion
  if (!isOpen || !promotion) return null

  // Format currency
  const formatCurrency = (amount) => {
    return `Rs ${parseFloat(amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`
  }

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    } catch {
      return 'Invalid date'
    }
  }

  // Format condition display
  const formatConditionDisplay = (condition) => {
    switch (condition.type) {
      case 'days_active':
        return `Active on: ${condition.daysActive?.map(day => 
          day.charAt(0).toUpperCase() + day.slice(1)
        ).join(', ') || 'N/A'}`

      case 'time_range':
        if (condition.timeRange) {
          return `Time: ${condition.timeRange.startTime} - ${condition.timeRange.endTime}`
        }
        return 'Time range not specified'

      case 'min_quantity':
        return `Minimum quantity: ${condition.minQuantity || 'N/A'}`

      case 'max_uses_per_order':
        return `Max uses per order: ${condition.maxUsesPerOrder || 'N/A'}`

      case 'min_order_value':
        return `Minimum order value: ${formatCurrency(condition.minOrderValue || 0)}`

      case 'payment_method':
        return `Payment methods: ${condition.paymentMethods?.map(method =>
          method.toUpperCase()
        ).join(', ') || 'N/A'}`

      default:
        return 'No additional details'
    }
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        {/* Backdrop */}
        <div 
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={onClose}
        />

        {/* Modal */}
        <div className="relative w-full max-w-4xl bg-white rounded-lg shadow-xl">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Promotion Details</h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <div className="p-4 max-h-[calc(90vh-120px)] overflow-y-auto space-y-4">
            {/* Basic Information */}
            <div>
              <h4 className="text-base font-semibold text-gray-900 mb-3">Basic Information</h4>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {/* Promotion Name */}
                <div className="bg-gray-50 rounded-lg p-3 col-span-2 md:col-span-1">
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Promotion Name
                  </label>
                  <div className="text-sm font-semibold text-gray-900 truncate">
                    {promotion.name}
                  </div>
                </div>

                {/* Type */}
                <div className="bg-gray-50 rounded-lg p-3">
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Type
                  </label>
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${promotion.typeBadgeClasses}`}>
                    {PromotionModel.getTypeIcon(promotion.promotionType)} {promotion.formattedType}
                  </span>
                </div>

                {/* Discount */}
                <div className="bg-gray-50 rounded-lg p-3">
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Discount
                  </label>
                  <div className="text-lg font-bold text-green-600">
                    {promotion.formattedDiscount}
                  </div>
                </div>

                {/* Priority */}
                <div className="bg-gray-50 rounded-lg p-3">
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Priority
                  </label>
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${promotion.priorityBadgeClasses}`}>
                    <Star className="h-3 w-3 mr-1" />
                    {promotion.formattedPriority}
                  </span>
                </div>

                {/* Target Audience */}
                <div className="bg-gray-50 rounded-lg p-3">
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Target Audience
                  </label>
                  <div className="flex items-center space-x-1">
                    <Users className="h-3 w-3 text-gray-400" />
                    <span className="text-sm text-gray-900 truncate">
                      {promotion.targetAudienceName}
                    </span>
                  </div>
                </div>

                {/* Status */}
                <div className="bg-gray-50 rounded-lg p-3">
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Status
                  </label>
                  <div className="flex flex-col space-y-1">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${promotion.statusBadgeClasses} w-fit`}>
                      {promotion.isActive ? 'Active' : 'Inactive'}
                    </span>
                    {promotion.isCurrentlyActive && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 w-fit">
                        <div className="w-1.5 h-1.5 bg-green-400 rounded-full mr-1 animate-pulse"></div>
                        Running
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Description */}
              {promotion.description && (
                <div className="mt-3 bg-gray-50 rounded-lg p-3">
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Description
                  </label>
                  <p className="text-sm text-gray-900 line-clamp-3">
                    {promotion.description}
                  </p>
                </div>
              )}
            </div>

            {/* Date Information */}
            <div>
              <h4 className="text-base font-semibold text-gray-900 mb-3 flex items-center">
                <Calendar className="h-4 w-4 mr-2" />
                Promotion Period
              </h4>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-blue-50 rounded-lg p-3">
                  <label className="block text-xs font-medium text-blue-600 mb-1">
                    Start Date
                  </label>
                  <div className="text-sm font-semibold text-blue-900">
                    {formatDate(promotion.startDate)}
                  </div>
                </div>

                <div className="bg-red-50 rounded-lg p-3">
                  <label className="block text-xs font-medium text-red-600 mb-1">
                    End Date
                  </label>
                  <div className="text-sm font-semibold text-red-900">
                    {formatDate(promotion.endDate)}
                  </div>
                </div>
              </div>

              {/* Time Remaining */}
              {promotion.isCurrentlyActive && promotion.daysRemaining !== null && (
                <div className="mt-3 bg-green-50 rounded-lg p-3">
                  <div className="flex items-center space-x-2">
                    <Clock className="h-4 w-4 text-green-600" />
                    <span className="text-sm font-semibold text-green-900">
                      {promotion.daysRemaining > 0 
                        ? `${promotion.daysRemaining} days remaining`
                        : 'Expires today'
                      }
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Products Section */}
            {promotion.products && promotion.products.length > 0 && (
              <div>
                <h4 className="text-base font-semibold text-gray-900 mb-3 flex items-center">
                  <Package className="h-4 w-4 mr-2" />
                  Products ({promotion.products.length})
                </h4>
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="grid grid-cols-1 gap-2">
                    {promotion.products.map((product, index) => (
                      <div key={product._id || index} className="flex items-center space-x-3 p-2 bg-white rounded border">
                        <Package className="h-5 w-5 text-gray-400 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm text-gray-900 truncate">
                            {product.name || 'Unknown Product'}
                          </div>
                          <div className="text-xs text-gray-500">
                            SKU: {product.sku || 'N/A'}
                          </div>
                        </div>
                        {product.outletPrice && (
                          <div className="text-xs font-medium text-gray-900 flex-shrink-0">
                            {formatCurrency(product.outletPrice)}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Conditions Section */}
            {promotion.conditions && promotion.conditions.length > 0 && (
              <div>
                <h4 className="text-base font-semibold text-gray-900 mb-3 flex items-center">
                  <Target className="h-4 w-4 mr-2" />
                  Conditions ({promotion.conditions.length})
                </h4>
                <div className="space-y-2">
                  {promotion.conditions.map((condition, index) => (
                    <div key={index} className="bg-blue-50 rounded-lg p-3 border border-blue-200">
                      <div className="flex items-start space-x-2">
                        <Target className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm text-blue-900">
                            {condition.type?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Unknown Condition'}
                          </div>
                          <div className="text-xs text-blue-700">
                            {formatConditionDisplay(condition)}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Metadata */}
            <div className="border-t border-gray-200 pt-3">
              <h4 className="text-base font-semibold text-gray-900 mb-3">Metadata</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="bg-gray-50 rounded-lg p-3">
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Created
                  </label>
                  <p className="text-sm text-gray-900">
                    {formatDate(promotion.createdAt)}
                    {promotion.createdBy && (
                      <span className="text-gray-500 block text-xs">
                        by {promotion.createdBy.username || 'Unknown User'}
                      </span>
                    )}
                  </p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Last Updated
                  </label>
                  <p className="text-sm text-gray-900">
                    {formatDate(promotion.updatedAt)}
                    {promotion.updatedBy && (
                      <span className="text-gray-500 block text-xs">
                        by {promotion.updatedBy.username || 'Unknown User'}
                      </span>
                    )}
                  </p>
                </div>
              </div>

              {/* Promotion ID */}
              <div className="mt-3 bg-gray-50 rounded-lg p-2">
                <p className="text-xs text-gray-500">
                  <span className="font-medium">ID:</span> {promotion._id}
                </p>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end p-4 pt-0">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors"
          >
            Close
          </button>
        </div>
        </div>
      </div>
    </div>
  )
}

export default ViewPromotionModal